import { inject, Injectable } from '@angular/core';
import { IDropDownMenuItem } from '../menu/menu.interface';
import {
  IAppDashboardTypeFlags,
  IPermissions,
  IMenuItemPermissions,
  IMenuItemModulePermission,
  IWidgetPermissions,
  IPluginPermissions,
  IUser,
  IUserRole,
  ModelService,
  IAppDashboard,
  IPermissionsFlags,
  IMenuItemSubModulePermission
} from '../../models';


@Injectable({
  providedIn: 'root'
})
export class PermissionService {
  private readonly adminRoleClass: string = 'admins';
  private readonly standardDashboardRef: string = 'standard_dashboard';
  private readonly customDashboardRef: string = 'custom_dashboard';
  private readonly appWidgetTypeFlags: { copy: IAppDashboardTypeFlags, view: IAppDashboardTypeFlags } = {
    copy: {
      edit: true,
      delete: true
    },
    view: {
      edit: false,
      delete: false
    }
  };

  private readonly modelService: ModelService = inject(ModelService);

 async getPermissions(appDashboard: IAppDashboard, user: IUser): Promise<IPluginPermissions[]> {
    const isAdmin = this.isAdmin(user);
    const dashboardTypePermissions = this.appWidgetTypeFlags[appDashboard.type];

    const permissions = await this.modelService._permissions.listAll('attr=user_role');

    if (isAdmin) {
      this.grantAdminPermissions(dashboardTypePermissions);
    }

    let pluginPermissions: IPluginPermissions[] = this.filterPermissionsByPluginId(
        user,
        permissions
    );

    this.updatePermissionsBasedOnDashboard(
        pluginPermissions,
        dashboardTypePermissions,
        this.isStandardDashboard(appDashboard)
    );

    return pluginPermissions;
  }

  private filterPermissionsByPluginId(user: IUser, permissions: IPermissions[]): IPluginPermissions[] {
    const activePluginPermissions: IPluginPermissions[] = [];

    // Filter permissions by user roles
    const permissionsOfRoles = permissions.filter((permission: IPermissions) =>
        user.roles.some((role: IUserRole) => role._id === permission.user_role._id)
    );

    permissionsOfRoles.forEach((permission: IPermissions) => {
      permission.plugin_permissions.forEach((pluginPermission: IPluginPermissions) => {
        const existingPermission = activePluginPermissions.find(
            (activePermission) => activePermission.plugin_id === pluginPermission.plugin_id
        );

        if (existingPermission) {
          this.mergePluginPermissions(existingPermission, pluginPermission);
        } else {
          activePluginPermissions.push(pluginPermission);
        }
      });
    });

    return activePluginPermissions;
  }

  private mergePluginPermissions(existingPermission: IPluginPermissions, newPermission: IPluginPermissions): void {
    if (newPermission.menuitem_permissions) {
      if (existingPermission.menuitem_permissions) {
        this.setPluginMenuItemPermissions(
            existingPermission.menuitem_permissions,
            newPermission.menuitem_permissions
        );
      } else {
        existingPermission.menuitem_permissions = newPermission.menuitem_permissions;
      }
    }

    if (newPermission.widget_permissions) {
      if (existingPermission.widget_permissions) {
        this.setPluginWidgetPermissions(
            existingPermission.widget_permissions,
            newPermission.widget_permissions
        );
      } else {
        existingPermission.widget_permissions = newPermission.widget_permissions;
      }
    }
  }

  private setPluginMenuItemPermissions(
      menuItemPermissions: IMenuItemPermissions,
      currMenuItemPermissions: IMenuItemPermissions
  ): void {
    for (const moduleKey in currMenuItemPermissions) {
      if (!menuItemPermissions[moduleKey]) {
        menuItemPermissions[moduleKey] = currMenuItemPermissions[moduleKey];
        continue;
      }

      this.mergePermissionsWithOr(
          menuItemPermissions[moduleKey],
          currMenuItemPermissions[moduleKey]
      );
    }
  }

  private setPluginWidgetPermissions(
      widgetPermissions: IWidgetPermissions,
      currWidgetPermissions: IWidgetPermissions
  ): void {
    this.mergePermissionsWithOr(widgetPermissions, currWidgetPermissions);
  }

  // Helper method to check if the user has admin role
  private isAdmin(user: IUser): boolean {
    return user.roles.some((role: IUserRole) => role.class === this.adminRoleClass);
  }

  // Granting admin-specific permissions
  private grantAdminPermissions(dashboardTypePermissions: IAppDashboardTypeFlags): void {
    dashboardTypePermissions.edit = true;
    dashboardTypePermissions.delete = true;
  }

  // Declare what constitutes a standard dashboard
  isStandardDashboard(appDashboard: IAppDashboard | undefined): boolean {
    return (appDashboard?.roleAssignable && appDashboard?.isPublished) || false;
  }

  isCustomDashboard(appDashboard: IAppDashboard | undefined): boolean {
    return (appDashboard?.isPublished === false || appDashboard?.isPublished == undefined) || false;
  }

  private isStandardDashboardManagementPermission(accessRight: string): boolean {
    return accessRight.includes(this.standardDashboardRef);
  }

  private isCustomDashboardManagementPermission(accessRight: string): boolean {
    return accessRight.includes(this.customDashboardRef);
  }

  // Ensure that custom-dashboard can not be deleted / edited if correct permission flags are not set
  private isDeniedStandardDashboardPermission(accessRef: string): boolean {
    return accessRef.includes('standard_dashboard_edit') ||
        accessRef.includes('standard_dashboard_delete');
  }

  // Ensure that standard-dashboard can not be deleted / edited if correct permission flags are not set
  private isDeniedCustomDashboardPermission(accessRef: string): boolean {
    return accessRef.includes('custom_dashboard_edit') ||
        accessRef.includes('custom_dashboard_delete');
  }

  // Update permissions based on dashboard type
  private updatePermissionsBasedOnDashboard(
      pluginPermissions: IPluginPermissions[],
      dashboardTypePermissions: IAppDashboardTypeFlags,
      isStandardDashboard: boolean
  ): void {
    pluginPermissions.forEach((pluginPermission: IPluginPermissions) => {
      // MenuItemPermissions
      if (pluginPermission?.menuitem_permissions) {
        for (const pluginModule in pluginPermission.menuitem_permissions) {
          const modulePermissions = pluginPermission.menuitem_permissions[pluginModule];

          for (const permissionKey in modulePermissions) {
            const value = modulePermissions[permissionKey];

            if (this.isPermissionFlagSet(value)) {
              // Nested flags (IPermissionsFlags)
              const nestedPermissions = value as IPermissionsFlags;
              for (const nestedKey in nestedPermissions) {
                const currentFlag: [string, boolean] = [nestedKey, nestedPermissions[nestedKey]];
                nestedPermissions[nestedKey] = this.isPermissionAllowedBasedOnDashboardType(
                    currentFlag,
                    dashboardTypePermissions,
                    isStandardDashboard
                );
              }
            } else {
              // Primitive boolean flag
              const currentFlag: [string, boolean] = [permissionKey, value as boolean];
              modulePermissions[permissionKey] = this.isPermissionAllowedBasedOnDashboardType(
                  currentFlag,
                  dashboardTypePermissions,
                  isStandardDashboard
              );
            }
          }
        }
      }

      // WidgetPermissions
      if (pluginPermission?.widget_permissions) {
        const widgetPermissions = pluginPermission.widget_permissions;

        for (const widgetKey in widgetPermissions) {
          const value = widgetPermissions[widgetKey];

          if (this.isPermissionFlagSet(value)) {
            const nestedWidgetPermissions = value as IPermissionsFlags;
            for (const nestedKey in nestedWidgetPermissions) {
              const currentFlag: [string, boolean] = [nestedKey, nestedWidgetPermissions[nestedKey]];
              nestedWidgetPermissions[nestedKey] = this.isPermissionAllowedBasedOnDashboardType(
                  currentFlag,
                  dashboardTypePermissions,
                  isStandardDashboard,
                  true
              );
            }
          } else {
            const currentFlag: [string, boolean] = [widgetKey, value as boolean];
            widgetPermissions[widgetKey] = this.isPermissionAllowedBasedOnDashboardType(
                currentFlag,
                dashboardTypePermissions,
                isStandardDashboard,
                true
            );
          }
        }
      }
    });
  }

  // Helper function that recursively searches for a permission key in a nested permission structure
  getPermissionValue(modulePermissions: IWidgetPermissions | IMenuItemPermissions, key: string): boolean {
    if (this.isPermissionFlagSet(modulePermissions)) {
      return modulePermissions[key];
    }

    for (const subKey in modulePermissions) {
      if (this.getPermissionValue(modulePermissions[subKey], key)) {
        return true;
      }
    }

    return false;
  }

  // Helper function to determine if an object is IPermissionsFlags
  isPermissionFlagSet(obj: any): obj is IPermissionsFlags {
    return obj && typeof obj === 'object' && Object.values(obj).every((v: unknown) => typeof v === 'boolean');
  }

  // Helper function to determine if an object is IMenuItemSubModulePermission
  isSubModulePermission(obj: any): obj is IMenuItemSubModulePermission {
    return obj && typeof obj === 'object' && Object.values(obj).every((v: unknown) => this.isPermissionFlagSet(v));
  }

  // Determine if permission is allowed based on dashboard type
  // Merge permission with AND
  private isPermissionAllowedBasedOnDashboardType(
    flag: [string, boolean],
    dashboardTypeFlags: IAppDashboardTypeFlags,
    isStandardDashboard: boolean,
    isWidgetPermission: boolean = false
  ): boolean {
    const [permission, isGranted] = flag;
    // Core-Plugin: Dashboard Module Permissions
    const isStandardDashboardPermission = this.isStandardDashboardManagementPermission(permission);

    if (permission.includes('edit') && !isStandardDashboardPermission) {
      // Special case: widget permission on standard dashboard
      if (isWidgetPermission && isStandardDashboard) {
        return isGranted;
      } else {
        return isGranted && dashboardTypeFlags.edit;
      }
    } else if (permission.includes('delete') && !isStandardDashboardPermission) {
      // Special case: widget permission on standard dashboard
      if (isWidgetPermission && isStandardDashboard) {
        return isGranted;
      } else {
        return isGranted && dashboardTypeFlags.delete;
      }
    }

    return isGranted;
  }

  // Merge permission with OR
  private mergePermissionsWithOr(
      targetPermissions: Record<string, boolean | IPermissionsFlags>,
      sourcePermissions: Record<string, boolean | IPermissionsFlags>
  ): void {
    for (const key in sourcePermissions) {
      const sourceValue: boolean | IPermissionsFlags = sourcePermissions[key];

      if (!(key in targetPermissions)) {
        // If the entry does not yet exist, accept it directly
        targetPermissions[key] = sourceValue;
        continue;
      }

      const targetValue: boolean | IPermissionsFlags = targetPermissions[key];

      if (this.isPermissionFlagSet(sourceValue)) {
        // Nested flags present
        const sourceNested = sourceValue as IPermissionsFlags;

        if (!this.isPermissionFlagSet(targetValue)) {
          // Initialize if no object exists yet
          targetPermissions[key] = {} as IPermissionsFlags;
        }

        const targetNested = targetPermissions[key] as IPermissionsFlags;

        for (const nestedKey in sourceNested) {
          // OR operation for nested flags
          const existingFlag = targetNested[nestedKey] ?? false;
          const sourceFlag = sourceNested[nestedKey];
          targetNested[nestedKey] = existingFlag || sourceFlag;
        }
      } else {
        // Combine flat Boolean flags directly with OR operation
        targetPermissions[key] = (targetValue as boolean) || (sourceValue as boolean);
      }
    }
  }

  // Validate menu permissions in nested structures
  checkMenuPermissionsRecursive(
      menuItemModulePermissions: IMenuItemModulePermission,
      menuAccessRights: string[]
  ): boolean {
    if (this.isPermissionFlagSet(menuItemModulePermissions)) {
      // Base case: modulePermissions is a flat object with boolean flags
      return menuAccessRights.some((permissionFlag: string) => menuItemModulePermissions[permissionFlag]);
    }

    // Recursive case: modulePermissions contains nested permission objects
    return Object.values(menuItemModulePermissions).some((subModule: IMenuItemModulePermission) =>
        this.checkMenuPermissionsRecursive(subModule, menuAccessRights)
    );
  }

  // Validate menu DropDown permissions in nested structures
	checkDropdownMenuPermissionsRecursive(
		menuItemModulePermissions: IMenuItemModulePermission,
		dropdown: IDropDownMenuItem,
		dashboard: IAppDashboard | undefined
	): boolean {
		if (this.isPermissionFlagSet(menuItemModulePermissions)) {
			return this.checkPermissionFlags(menuItemModulePermissions, dropdown, dashboard);
		}

		// For sub-modules, check each one
		for (const subModuleKey in menuItemModulePermissions) {
			const subModule = menuItemModulePermissions[subModuleKey];
			if (this.checkDropdownMenuPermissionsRecursive(subModule, dropdown, dashboard)) {
				return true;
			}
		}

		return false;
	}

  // Validate explicit permissions flags
	private checkPermissionFlags(
		permissionFlags: IPermissionsFlags,
		dropdown: IDropDownMenuItem,
		dashboard: IAppDashboard | undefined
	): boolean {
		if (typeof dropdown.accessReference === "string") {
			// Single permission reference - must exist in this permission set
			return permissionFlags[dropdown.accessReference] === true;
		}

		let hasAccess = false;
		if (Array.isArray(dropdown.accessReference)) {
			dropdown.accessReference?.forEach((accessRef: string) => {
				// Only check permissions that exist in this permission flags object
				if (!(accessRef in permissionFlags)) {
					return; // Skip permissions not in this scope
				}

				// is standard-dashboard and accessRef is standard-dashboard permission
				if (this.isStandardDashboard(dashboard) &&
					this.isStandardDashboardManagementPermission(accessRef) &&
					permissionFlags[accessRef] !== undefined
				) {
					hasAccess = hasAccess || permissionFlags[accessRef];
				}

				// is standard-dashboard and accessRef is custom-dashboard permission (but not of the denied ones)
				if (this.isStandardDashboard(dashboard) &&
					this.isCustomDashboardManagementPermission(accessRef) &&
					!this.isDeniedCustomDashboardPermission(accessRef) &&
					permissionFlags[accessRef] !== undefined
				) {
					hasAccess = hasAccess || permissionFlags[accessRef];
				}

				// is custom-dashboard and accessRef is no denied standard-dashboard permission
				if (this.isCustomDashboard(dashboard) &&
					!this.isDeniedStandardDashboardPermission(accessRef) &&
					permissionFlags[accessRef] !== undefined
				) {
					hasAccess = hasAccess || permissionFlags[accessRef];
				}
			})
		}

		return hasAccess;
	}
}
